<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>Choose Schema</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="$emit('close')">Cancel</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div v-if="loading" class="csm-loading">
        <ion-spinner />
      </div>

      <ion-list v-else>
        <ion-list-header>
          <ion-label>Global schemas</ion-label>
        </ion-list-header>
        <ion-item
          v-for="schema in globalSchemas"
          :key="schema.id"
          button
          :disabled="appliedSchemaIds.includes(schema.id)"
          @click="$emit('save', schema.id)"
        >
          <ion-icon :icon="libraryOutline" slot="start" />
          <ion-label>
            <h3>{{ schema.name }}</h3>
            <p v-if="schema.description">{{ schema.description }}</p>
            <p v-if="appliedSchemaIds.includes(schema.id)" style="color: var(--ion-color-medium)">Already applied</p>
          </ion-label>
          <ion-badge v-if="schema.entries.length" color="light" slot="end">{{ schema.entries.length }} fields</ion-badge>
        </ion-item>
        <ion-item v-if="globalSchemas.length === 0">
          <ion-label style="color: var(--ion-color-medium)">No global schemas yet.</ion-label>
        </ion-item>

        <ion-list-header v-if="privateSchemas.length">
          <ion-label>My private schemas</ion-label>
        </ion-list-header>
        <ion-item
          v-for="schema in privateSchemas"
          :key="schema.id"
          button
          :disabled="appliedSchemaIds.includes(schema.id)"
          @click="$emit('save', schema.id)"
        >
          <ion-icon :icon="lockClosedOutline" slot="start" />
          <ion-label>
            <h3>{{ schema.name }}</h3>
            <p v-if="schema.description">{{ schema.description }}</p>
          </ion-label>
          <ion-badge v-if="schema.entries.length" color="light" slot="end">{{ schema.entries.length }} fields</ion-badge>
        </ion-item>
      </ion-list>

      <div class="csm-new-btn">
        <ion-button expand="block" fill="outline" @click="openCreate">
          <ion-icon slot="start" :icon="addOutline" />
          Create new schema
        </ion-button>
      </div>

      <ion-modal
        :is-open="createSchemaOpen"
        :can-dismiss="canDismissExceptGesture"
        @did-dismiss="createSchemaOpen = false"
      >
        <SchemaEditorModal
          :initial-draft="createDraft"
          @save="onSchemaCreated"
          @close="createSchemaOpen = false"
        />
      </ion-modal>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { canDismissExceptGesture } from "@/helper/modalDismiss";
import { computed, onMounted, ref } from "vue";
import {
  IonBadge, IonButton, IonButtons, IonContent, IonHeader, IonIcon,
  IonItem, IonLabel, IonList, IonListHeader, IonModal, IonPage, IonSpinner, IonTitle, IonToolbar,
} from "@ionic/vue";
import { addOutline, libraryOutline, lockClosedOutline } from "ionicons/icons";
import { useApiStore } from "@/stores/api";
import type { CustomSchemaOut } from "@/models/custom-schemas";
import type { CustomSchemaId } from "@/models/branded-ids";
import SchemaEditorModal from "@/components/modals/SchemaEditorModal.vue";
import { askToContinueDraft } from "@/helper/draftPrompt";
import type { Draft } from "@/helper/drafts";

defineProps<{
  objectId: number;
  appliedSchemaIds: CustomSchemaId[];
}>();

const emit = defineEmits<{
  save: [schemaId: CustomSchemaId];
  close: [];
}>();

const api = useApiStore();
const loading = ref(false);
const createSchemaOpen = ref(false);
const createDraft = ref<Draft | null>(null);

async function openCreate() {
  const { choice, draft } = await askToContinueDraft("schema-create", "new");
  if (choice === "abort") return;
  createDraft.value = draft;
  createSchemaOpen.value = true;
}

const globalSchemas = computed(() => api.schemas.filter((s) => s.visibility === "global"));
const privateSchemas = computed(() => api.schemas.filter((s) => s.visibility === "private"));

onMounted(async () => {
  loading.value = true;
  await api.fetchSchemas();
  loading.value = false;
});

async function onSchemaCreated(schema: CustomSchemaOut) {
  createSchemaOpen.value = false;
  emit("save", schema.id);
}
</script>

<style scoped>
.csm-loading {
  display: flex;
  justify-content: center;
  padding: 32px;
}

.csm-new-btn {
  padding: 12px;
}
</style>
